import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Image,
  Popconfirm,
  Tooltip,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MiniQuizQuestion } from '@/types';
import { formatDistanceToNow } from 'date-fns';

const { Text } = Typography;

interface QuestionsTableProps {
  questions: MiniQuizQuestion[];
  loading: boolean;
  pagination: TablePaginationConfig;
  onEdit: (question: MiniQuizQuestion) => void;
  onDelete: (id: number) => void;
  onBulkStatusUpdate: (questionIds: number[], isActive: boolean) => void;
  onTableChange: (page: number, pageSize: number) => void;
}

const QuestionsTable: React.FC<QuestionsTableProps> = ({
  questions,
  loading,
  pagination,
  onEdit,
  onDelete,
  onBulkStatusUpdate,
  onTableChange,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const handleSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys as number[]);
  };

  const handleBulkActivate = () => {
    if (selectedRowKeys.length > 0) {
      onBulkStatusUpdate(selectedRowKeys, true);
      setSelectedRowKeys([]);
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedRowKeys.length > 0) {
      onBulkStatusUpdate(selectedRowKeys, false);
      setSelectedRowKeys([]);
    }
  };

  const columns: ColumnsType<MiniQuizQuestion> = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (imageUrl: string) => (
        <Image
          src={imageUrl}
          crossOrigin='anonymous'
          alt="Question"
          width={60}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text style={{ maxWidth: 200 }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Correct Answer',
      dataIndex: 'correctAnswer',
      key: 'correctAnswer',
      width: 120,
      align: 'center',
      render: (correctAnswer: boolean) => (
        <Tag color={correctAnswer ? 'green' : 'red'}>
          {correctAnswer ? 'TRUE' : 'FALSE'}
        </Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      align: 'center',
      render: (category: string) => (
        category ? (
          <Tag color="blue">{category}</Tag>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            No category
          </Text>
        )
      ),
    },
    {
      title: 'Explanation',
      dataIndex: 'explanation',
      key: 'explanation',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        text ? (
          <Tooltip title={text}>
            <Text style={{ maxWidth: 180 }}>{text}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            No explanation
          </Text>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag
          color={isActive ? 'success' : 'default'}
          icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}
        >
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <Text type="secondary">
            {formatDistanceToNow(new Date(date), { addSuffix: true })}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record: MiniQuizQuestion) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Question"
              description="Are you sure you want to delete this question?"
              onConfirm={() => onDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectChange,
    getCheckboxProps: (record: MiniQuizQuestion) => ({
      disabled: false,
      name: record.id.toString(),
    }),
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div>
      {/* Bulk Actions */}
      {hasSelected && (
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Space>
              <Text strong>{selectedRowKeys.length} questions selected</Text>
              <Button
                type="primary"
                ghost
                icon={<CheckCircleOutlined />}
                onClick={handleBulkActivate}
              >
                Activate Selected
              </Button>
              <Button
                icon={<StopOutlined />}
                onClick={handleBulkDeactivate}
              >
                Deactivate Selected
              </Button>
            </Space>
          </Col>
        </Row>
      )}

      {/* Table */}
      <Table
        columns={columns}
        dataSource={questions}
        loading={loading}
        rowKey="id"
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          onChange: onTableChange,
          onShowSizeChange: onTableChange,
        }}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};

export default QuestionsTable;
